import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { PermissionViewStore } from "../../store";
import { PermissionView } from "../view/PermissionView";
import { PermissionUpdateStore } from "../../store";
import { PermissionUpdate } from "../update/PermissionUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type PermissionModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const PermissionModalContent: React.FC<PermissionModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new PermissionUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new PermissionViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <PermissionView
            permissionViewStore={storeInfo.store as PermissionViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <PermissionUpdate
            permissionUpdateStore={storeInfo.store as PermissionUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
